import React, { useCallback, useEffect, useMemo } from 'react'
import clsx from 'clsx'
import { Button, Col, Grid, Icon, Range, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import { Field, FieldArray, reduxForm } from 'redux-form'
import validator from 'utils/asyncValidator'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import validationSchema from 'modules/Forms/Referral/LinkForm/validationSchema'
import renderInputField from 'components/FormControls/textInput'
import FinancialTerms from 'modules/Forms/Referral/LinkForm/subcomponents/FinancialTerms'
import { onlyNumbers } from 'utils/normalize/onlyNumbers'
import { useReferralLinks } from 'modules/Referral/ReferralLinks/hooks/useReferralLinks'
import { toFixedIfNecessary } from 'utils/toFixedIfNecessary'
import useWriteToClipboard from 'utils/hooks/useWriteToClipboard'
import formStyles from 'modules/Forms/common-form.module.scss'
import { notificationShow } from 'modules/Notifications/actions'

const DEFAULT_RATE = 50
const MIN_RATE = 0
const MAX_RATE = 100

let LinkForm = ({
  handleSubmit,
  submitting,
  onClose = () => {},
  form: formName,
  autofill,
  settings = [],
  name = null,
  id = null,
  url = null,
}) => {
  const { values: { client_rate, partner_rate } = {} } = useSelector(({ form = {} }) => ({
    values: form?.[formName]?.values || {},
  }))
  const writeToClipboard = useWriteToClipboard()
  const isEditForm = useMemo(() => id !== null, [id])
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { createLink, updateLink } = useReferralLinks()

  const submit = useCallback(
    (values) => {
      const { name, settings } = values
      if (isEditForm) {
        updateLink({
          formName,
          id,
          data: { name },
        }).then(onClose)
      } else {
        createLink({
          formName,
          name,
          settings: settings.map(
            ({ financial_profile_type_alias, adnetwork: { id }, client_percent }) => ({
              financial_profile_type_alias,
              adnetwork_id: id,
              client_percent: +client_percent,
            }),
          ),
        }).then(onClose)
      }
    },
    [isEditForm, id],
  )

  const handleRangeInput = useCallback((value) => autofill('client_rate', +value), [])

  useEffect(() => autofill('settings', settings), [settings])

  useEffect(() => {
    if (!name) {
      return
    }
    autofill('name', name)
  }, [name])

  useEffect(() => {
    if (isEditForm) {
      return
    }
    autofill('partner_rate', MAX_RATE - client_rate)
    autofill(
      'settings',
      settings.reduce((acc, { total_percent, ...rest }) => {
        const clientPercent = (total_percent * +client_rate) / MAX_RATE
        const partnerPercent = total_percent - clientPercent

        return [
          ...acc,
          {
            ...rest,
            client_percent: +toFixedIfNecessary(clientPercent),
            partner_percent: +toFixedIfNecessary(partnerPercent),
            total_percent,
          },
        ]
      }, []),
    )
  }, [client_rate, isEditForm])

  useEffect(() => autofill('client_rate', MAX_RATE - partner_rate), [partner_rate])

  useEffect(() => {
    autofill('client_rate', DEFAULT_RATE)
    autofill('partner_rate', MAX_RATE - DEFAULT_RATE)
  }, [])

  const onClickHandle = useCallback(
    (text) => {
      writeToClipboard(text)

      dispatch(
        notificationShow({
          type: 'success',
          message: 'referral::referral_links.notifications.success_copy_notification',
        }),
      )
    },
    [writeToClipboard],
  )

  const normalizeValue = useCallback((value) => {
    const number = onlyNumbers(value)
    return isNaN(+number) || +number > MAX_RATE
      ? MAX_RATE
      : number === '' || +number < 0
      ? MIN_RATE
      : number
  }, [])

  return (
    <div className={formStyles['formContainer']}>
      <div className={formStyles['formContainer__inner']}>
        <Typography
          className={'margin-bottom-16'}
          variant={'Header-60'}
          color={'Label-Primary'}
          component={'h1'}
        >
          {t(`referral::referral_links_${isEditForm ? 'edit' : 'create'}.heading`)}
        </Typography>
        <Typography
          className={'margin-bottom-64'}
          variant={'Body-20'}
          color={'Label-Secondary'}
        >
          {t('referral::referral_links_create.summary')}
        </Typography>
        {isEditForm && url && (
          <Grid
            fluid={false}
            className={'margin-bottom-32'}
            direction={'column'}
            gap={8}
          >
            <Typography
              component={'div'}
              variant={'Subtitle-20'}
              color={'Label-Fifth'}
            >
              {t('referral::referral_links_edit.link')}
            </Typography>

            <Button
              onClick={() => onClickHandle(url)}
              type={'button'}
              variant={'ghost'}
              endIcon={<Icon name={'copy'} />}
              link
            >
              {url}
            </Button>
          </Grid>
        )}
        <form onSubmit={handleSubmit(submit)}>
          <div className={'margin-bottom-32'}>
            <Field
              label={t('referral::referral_links_create.form.fields.name.label')}
              type="text"
              name="name"
              component={renderInputField}
              fullWidth
              t={t}
            />
          </div>
          <Grid
            wrap={false}
            className={'margin-bottom-8'}
            gap={4}
          >
            <Col flexGrow={1}>
              <Field
                label={t('referral::referral_links_create.form.fields.partner_percent.label')}
                type="text"
                name="partner_rate"
                component={renderInputField}
                inputMode={'numeric'}
                fullWidth
                normalize={normalizeValue}
                format={(value) => value + '%'}
                maxLength={4}
                t={t}
                disabled={isEditForm}
              />
            </Col>
            <Grid
              fluid={false}
              alignItems={'center'}
              justifyContent={'center'}
            >
              <div className={formStyles['separator']} />
            </Grid>
            <Col flexGrow={1}>
              <Field
                label={t('referral::referral_links_create.form.fields.client_percent.label')}
                type="text"
                name="client_rate"
                component={renderInputField}
                fullWidth
                normalize={normalizeValue}
                inputMode={'numeric'}
                format={(value) => value + '%'}
                maxLength={4}
                t={t}
                disabled={isEditForm}
              />
            </Col>
          </Grid>

          <Range
            className={'margin-bottom-40'}
            value={client_rate}
            min={MIN_RATE}
            max={MAX_RATE}
            onInput={handleRangeInput}
            tooltip={false}
            disabled={isEditForm}
          />

          <FieldArray
            name={'settings'}
            component={FinancialTerms}
            className={'margin-bottom-64'}
            isEditForm={isEditForm}
            rerenderOnEveryChange
          />

          <div className={clsx(formStyles['formControl__action-buttons'])}>
            <Button
              disabled={submitting}
              onClick={onClose}
              variant={'ghost'}
              grey
              size="l"
              type="button"
            >
              {t('common::form_buttons.cancel')}
            </Button>
            <Button
              disabled={submitting}
              size="l"
              variant="primary"
              type="submit"
              bgColor={'Label-GU-Primary'}
            >
              {t(`common::form_buttons.${isEditForm ? 'save' : 'generateLink'}`)}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

LinkForm = reduxForm({
  form: 'LinkForm',
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  forceUnregisterOnUnmount: true,
  updateUnregisteredFields: true,
  shouldAsyncValidate: shouldAsyncValidate,
  asyncValidate: validator(validationSchema),
  initialValues: {
    partner_rate: DEFAULT_RATE,
    client_rate: DEFAULT_RATE,
    settings: [],
  },
})(LinkForm)

LinkForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  form: PropTypes.string,
  anyTouched: PropTypes.bool,
  change: PropTypes.func,
  financialTerms: PropTypes.array,
  autofill: PropTypes.func,
  settings: PropTypes.array,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  name: PropTypes.string,
  url: PropTypes.string,
}

export default LinkForm
